import type { Accelerometer, DeviceReading, PTH } from "@/types/device"
import type { ReadingPoint } from "@/hooks/use-device-status"

/**
 * Parse SG reading from BLE characteristic value
 * Format: Float32 x3 (specific gravity, tilt angle, temperature)
 */
export function parseSgReading(value: DataView): {
  specificGravity: number
  tiltAngle: number
  temperature: number
} {
  return {
    specificGravity: value.getFloat32(0, true), // little-endian
    tiltAngle: value.getFloat32(4, true),
    temperature: value.getFloat32(8, true),
  }
}

/**
 * Parse accelerometer data from BLE characteristic value
 * Format: Int16 x3 (x, y, z)
 */
export function parseAccelerometer(value: DataView): Accelerometer {
  return {
    x: value.getInt16(0, true),
    y: value.getInt16(2, true),
    z: value.getInt16(4, true),
  }
}

/**
 * Parse PTH (pressure, temperature, humidity) from BLE characteristic value
 * Format: Float32 x3 (temperature, pressure, humidity)
 */
export function parsePth(value: DataView): PTH {
  return {
    temp: value.getFloat32(0, true),
    pressure: value.getFloat32(4, true),
    humidity: value.getFloat32(8, true),
  }
}

/**
 * Parse battery voltage from BLE characteristic value
 * Format: Float32 (voltage)
 */
export function parseBatteryVoltage(value: DataView): number {
  return value.getFloat32(0, true)
}

/**
 * Convert temperature from Celsius to Fahrenheit
 */
export function celsiusToFahrenheit(celsius: number): number {
  return celsius * 9 / 5 + 32
}

/**
 * Convert temperature from Fahrenheit to Celsius
 */
export function fahrenheitToCelsius(fahrenheit: number): number {
  return (fahrenheit - 32) * 5 / 9
}

/**
 * Format specific gravity for display
 */
export function formatSpecificGravity(sg: number | null): string {
  if (sg === null) return "--"
  return sg.toFixed(3)
}

/**
 * Format temperature for display
 */
export function formatTemperature(
  temp: number | null,
  unit: "celsius" | "fahrenheit" = "fahrenheit"
): string {
  if (temp === null) return "--"
  const value = unit === "fahrenheit" ? celsiusToFahrenheit(temp) : temp
  const unitSymbol = unit === "fahrenheit" ? "F" : "C"
  return `${value.toFixed(1)}°${unitSymbol}`
}

/**
 * Format tilt angle for display
 */
export function formatTiltAngle(angle: number | null): string {
  if (angle === null) return "--"
  return `${angle.toFixed(1)}°`
}

/**
 * Format battery voltage for display
 */
export function formatBatteryVoltage(voltage: number | null): string {
  if (voltage === null) return "--"
  return `${voltage.toFixed(2)}V`
}

/**
 * Convert a server-rendered DeviceReading (snake_case, ISO timestamp) into
 * the in-memory ReadingPoint shape consumed by the readings buffer and
 * sparkline.
 */
export function toReadingPoint(reading: DeviceReading): ReadingPoint {
  return {
    t: Date.parse(reading.created_at),
    sg: reading.specific_gravity,
    temp: reading.temperature,
    tilt: reading.tilt_angle,
    battery: reading.battery_voltage,
  }
}
